/**
 * Inline citation chip rendered next to assistant claims.
 *
 * R-143-CHAT-ASSISTANT Phase D.
 *
 * Active evidence is clickable when the parent supplies onResolve. Historic
 * BoP simulation citations render only as a retirement notice and can never
 * navigate or be silently mapped to an EO run.
 */
import React from 'react';
import {
  FileText,
  Ban,
  ClipboardList,
  Banknote,
  Gauge,
  GitCompareArrows,
  Activity,
} from 'lucide-react';

import type { Citation, CitationKind } from '../../types/chat';

interface Props {
  citation: Citation;
  onResolve?: (citation: Citation) => void;
}

const KIND_ICONS: Record<CitationKind, React.ComponentType<{ className?: string }>> = {
  'sed-section': ClipboardList,
  'intent-field': FileText,
  'sim-run': Ban,
  'lcoe-run': Banknote,
  'evidence-doc': FileText,
  'report': FileText,
  'pathway-comparison': GitCompareArrows,
  'generation-scenario': Gauge,
};

const KIND_LABEL: Record<CitationKind, string> = {
  'sed-section': 'SED',
  'intent-field': 'Intent',
  'sim-run': 'Retired',
  'lcoe-run': 'LCOE',
  'evidence-doc': 'Doc',
  'report': 'Report',
  'pathway-comparison': 'Pathway',
  'generation-scenario': 'Generation',
};

export function CitationChip({ citation, onResolve }: Props): React.JSX.Element {
  const Icon = KIND_ICONS[citation.kind] ?? Activity;
  const kindLabel = KIND_LABEL[citation.kind] ?? 'Source';
  const retiredSimulation = citation.kind === 'sim-run' || Boolean(citation.simulationId);
  if (retiredSimulation) {
    return (
      <span
        role="note"
        title="Historic BoP execution evidence is no longer active. Regenerate the report or analysis from a validated run."
        className="mx-0.5 inline-flex items-center gap-1 rounded-md border border-warning/40 bg-warning/10 px-1.5 py-0.5 align-baseline type-meta font-semibold text-warning"
        data-testid={`citation-chip-${citation.id}`}
      >
        <Ban className="h-3 w-3 shrink-0" aria-hidden="true" />
        <span>Legacy simulation reference retired</span>
      </span>
    );
  }
  const interactive = !!onResolve;
  return (
    <button
      type="button"
      onClick={() => onResolve?.(citation)}
      disabled={!interactive}
      title={citation.excerpt ? citation.excerpt : citation.label}
      className="mx-0.5 inline-flex items-center gap-1 rounded-md border border-primary/40 bg-primary/5 px-1.5 py-0.5 align-baseline type-meta font-semibold text-primary hover:bg-primary/10 disabled:cursor-default"
      data-testid={`citation-chip-${citation.id}`}
    >
      <Icon className="h-3 w-3 shrink-0" aria-hidden="true" />
      <span>{kindLabel}</span>
      <span className="max-w-[10rem] truncate">
        {citation.label}
      </span>
    </button>
  );
}

/**
 * Replace [cite:id] markers in a markdown body with React-rendered
 * CitationChips. Returns an array of (string | ReactNode) the parent
 * weaves into the markdown stream.
 */
export function interpolateCitations(
  body: string,
  citations: Citation[],
  onResolve?: (citation: Citation) => void,
): React.ReactNode[] {
  const map = new Map(citations.map((c) => [c.id, c] as const));
  const parts: React.ReactNode[] = [];
  let i = 0;
  const re = /\[cite:([^\]]+)\]/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(body)) !== null) {
    if (m.index > i) parts.push(body.slice(i, m.index));
    const citation = map.get(m[1]);
    parts.push(
      citation ? (
        <CitationChip key={`c-${m.index}-${m[1]}`} citation={citation} onResolve={onResolve} />
      ) : (
        `[${m[1]}]`
      ),
    );
    i = m.index + m[0].length;
  }
  if (i < body.length) parts.push(body.slice(i));
  return parts;
}
